import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { LuCheck, LuChevronDown, LuPencil, LuPlus, LuSearch } from "react-icons/lu";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import CatalogueModalDropdownMenu from "components/CatalogueModalDropdownMenu";
import "./themedSelect.css";

let nextListboxId = 0;

// Shared themed select used by document flows, filters, and catalogue dialogs.
// It preserves the native select-like onChange(event) contract.
export default function ThemedSelect({
  children,
  dark,
  className = "",
  value,
  onChange,
  displayValue,
  errorMessage,
  disabled = false,
  contactType,
  menuBoundarySelector,
  menuPortalContainerSelector,
  disableMenuFlip = false,
  open: controlledOpen,
  onOpenChange,
  onSearchChange,
  onEditOption,
  ...props
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [listboxId] = useState(
    () => `themed-select-options-${++nextListboxId}`
  );
  const errorId = props.id ? `${props.id}-error` : `${listboxId}-error`;
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(open) : updater;
      if (onOpenChange) onOpenChange(next);
      if (!isControlled) setInternalOpen(next);
    },
    [open, onOpenChange, isControlled]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const keyboardOpenRef = useRef(null);
  const menuRef = useRef(null);
  const clearingContactRef = useRef(false);
  const restoringFocusRef = useRef(false);
  const options = Children.toArray(children)
    .filter(isValidElement)
    .map((option) => ({
      value: option.props.value ?? option.props.children,
      label: option.props.children,
      disabled: option.props.disabled,
      hidden: option.props.hidden,
      description: option.props["data-description"],
      status: option.props["data-status"],
    }));
  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0];

  useEffect(() => {
    if (controlledOpen) {
      setSearchQuery(value ? String(selected?.label || "") : "");
    }
  }, [controlledOpen, value, selected?.label]);

  const restoreFocus = useCallback(() => {
    const trigger =
      triggerRef.current || rootRef.current?.querySelector('[role="combobox"]');
    if (trigger && trigger !== document.activeElement) {
      restoringFocusRef.current = Boolean(contactType);
      trigger.focus();
    }
  }, [contactType]);
  const closeMenu = () => {
    setOpen(false);
    restoreFocus();
  };

  const focusOption = useCallback((key) => {
    const entries = Array.from(
      menuRef.current?.querySelectorAll('[role="option"]:not(:disabled)') || []
    );
    if (!entries.length) return;
    const current = entries.indexOf(document.activeElement);
    const selectedIndex = entries.findIndex(
      (entry) => entry.getAttribute("aria-selected") === "true"
    );
    const next =
      key === "Home"
        ? 0
        : key === "End"
        ? entries.length - 1
        : current < 0
        ? selectedIndex >= 0
          ? selectedIndex
          : key === "ArrowUp"
          ? entries.length - 1
          : 0
        : (current + (key === "ArrowUp" ? -1 : 1) + entries.length) %
          entries.length;
    entries[next].focus({ preventScroll: true });
    entries[next].scrollIntoView?.({ block: "nearest" });
  }, []);
  const navigateMenu = (event) => {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      focusOption(event.key);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
    } else if (event.key === "Tab") {
      closeMenu();
    }
  };
  useEffect(() => {
    if (open && keyboardOpenRef.current) {
      focusOption(keyboardOpenRef.current);
      keyboardOpenRef.current = null;
    }
  }, [open, focusOption]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    const closeOnOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      )
        setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        restoreFocus();
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, disabled, setOpen, restoreFocus]);

  if (contactType) {
    const addOption = options.find((option) => option.value === "add-new");
    const matches = options.filter(
      (option) =>
        !option.hidden &&
        option.value !== "add-new" &&
        `${option.label} ${option.description || ""}`
          .toLowerCase()
          .includes(searchQuery.trim().toLowerCase())
    );
    const chooseOption = (option) => {
      clearingContactRef.current = false;
      onChange?.({ target: { value: option.value } });
      closeMenu();
    };
    return (
      <div
        className={`quotation-figma-dropdown invoice-contact-search${
          open ? " is-open" : ""
        }${dark ? " dark" : ""} ${className}`}
        ref={rootRef}
      >
        <div
          className={`quotation-input-box${
            props["aria-invalid"] ? " error" : ""
          }`}
          data-clickable={!disabled}
          onClick={() => {
            if (!disabled) {
              if (!open)
                setSearchQuery(value ? String(selected?.label || "") : "");
              setOpen((current) => !current);
            }
          }}
        >
          <LuSearch />
          <ClearableSearchInput
            id={props.id}
            aria-invalid={props["aria-invalid"]}
            aria-describedby={
              props["aria-describedby"] || (errorMessage ? errorId : undefined)
            }
            aria-label={props["aria-label"]}
            role="combobox"
            aria-controls={open && !disabled ? listboxId : undefined}
            aria-expanded={open && !disabled}
            aria-autocomplete="list"
            placeholder={props.placeholder || `Select or search ${contactType}`}
            value={
              open
                ? searchQuery
                : displayValue ?? (value ? String(selected?.label || "") : "")
            }
            disabled={disabled}
            onChange={(event) => {
              clearingContactRef.current = event.target.value === "";
              setSearchQuery(event.target.value);
              onSearchChange?.(event.target.value);
              setOpen(true);
              if (event.target.value === "" && (value || displayValue)) {
                onChange?.({ target: { value: "" } });
              }
            }}
            onFocus={() => {
              if (restoringFocusRef.current) {
                restoringFocusRef.current = false;
                return;
              }
              if (!open && !clearingContactRef.current)
                setSearchQuery(value ? String(selected?.label || "") : "");
              clearingContactRef.current = false;
              setOpen(true);
            }}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (["ArrowDown", "ArrowUp"].includes(event.key)) {
                event.preventDefault();
                event.stopPropagation();
                if (open) focusOption(event.key);
                else {
                  keyboardOpenRef.current = event.key;
                  setOpen(true);
                }
              } else if (event.key === "Escape" && open) {
                event.preventDefault();
                event.stopPropagation();
                closeMenu();
              }
            }}
          />
          <LuChevronDown />
        </div>
        {errorMessage && (
          <span id={errorId} className="quotation-field-error" role="alert">
            {errorMessage}
          </span>
        )}
        {open && !disabled && (
          <CatalogueModalDropdownMenu anchorEl={rootRef.current}
            boundaryEl={menuBoundarySelector ? rootRef.current?.closest(menuBoundarySelector) : undefined}
            containerEl={menuPortalContainerSelector ? rootRef.current?.closest(menuPortalContainerSelector) : undefined}
            disableFlip={disableMenuFlip}>
          <div
            className="quotation-autocomplete-dropdown"
            ref={menuRef}
            onKeyDown={navigateMenu}
            id={listboxId}
            role="listbox"
            aria-label={props["aria-label"]}
          >
            {addOption && (
              <button
                type="button"
                className="quotation-autocomplete-add-action"
                disabled={addOption.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  chooseOption(addOption);
                }}
              >
                <LuPlus />
                <span>{addOption.label}</span>
              </button>
            )}
            {matches.length ? (
              matches.map((option) => {
                const optionButton = (
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(option.value) === String(value)}
                    className={
                      onEditOption
                        ? "quotation-customer-option-select"
                        : "quotation-autocomplete-item"
                    }
                    key={option.value}
                    disabled={option.disabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      chooseOption(option);
                    }}
                  >
                    <strong>{option.label}</strong>
                    {(option.description || contactType !== "document") && (
                      <span>{option.description || "No email"}</span>
                    )}
                  </button>
                );
                return onEditOption ? (
                  <div
                    key={option.value}
                    className="quotation-autocomplete-item quotation-customer-option"
                  >
                    {optionButton}
                    <div className="quotation-customer-option-actions">
                      <button
                        type="button"
                        aria-label={`Edit ${option.label}`}
                        title="Edit customer"
                        disabled={option.disabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpen(false);
                          onEditOption(option.value);
                        }}
                      >
                        <LuPencil aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ) : (
                  optionButton
                );
              })
            ) : (
              <div className="quotation-autocomplete-no-match">
                <strong>No {contactType}s found</strong>
                <span>
                  Try a different name
                  {addOption ? ` or add a new ${contactType}` : ""}.
                </span>
              </div>
            )}
          </div>
          </CatalogueModalDropdownMenu>
        )}
      </div>
    );
  }

  return (
    <div
      className={`quotation-figma-dropdown${open ? " is-open" : ""}${
        dark ? " dark" : ""
      }${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`quotation-figma-dropdown-trigger${
          props["aria-invalid"] || errorMessage ? " error" : ""
        }`}
        id={props.id}
        aria-describedby={
          props["aria-describedby"] || (errorMessage ? errorId : undefined)
        }
        aria-label={props["aria-label"] || "Select an option"}
        aria-haspopup="listbox"
        aria-controls={open && !disabled ? listboxId : undefined}
        aria-expanded={open && !disabled}
        ref={triggerRef}
        disabled={disabled}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
            return;
          }
          if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            if (open) focusOption(event.key);
            else {
              keyboardOpenRef.current = event.key;
              setOpen(true);
            }
          }
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) setOpen((current) => !current);
        }}
      >
        <span>
          {displayValue != null && displayValue !== ""
            ? displayValue
            : selected?.label ?? ""}
        </span>
        <LuChevronDown />
      </button>
      {errorMessage && (
        <span id={errorId} className="quotation-field-error" role="alert">
          {errorMessage}
        </span>
      )}
      {open && !disabled && (
        <CatalogueModalDropdownMenu
          anchorEl={rootRef.current}
          boundaryEl={
            menuBoundarySelector
              ? rootRef.current?.closest(menuBoundarySelector)
              : undefined
          }
          containerEl={
            menuPortalContainerSelector
              ? rootRef.current?.closest(menuPortalContainerSelector)
              : undefined
          }
          disableFlip={disableMenuFlip}
        >
          <div
            className="quotation-figma-dropdown-menu"
            ref={menuRef}
            id={listboxId}
            onKeyDown={navigateMenu}
            role="listbox"
            aria-label={props["aria-label"] || "Select an option"}
          >
            {options
              .filter((option) => !option.hidden)
              .map((option, index) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-label={option.status && typeof option.label === "string" ? option.label : undefined}
                    aria-describedby={option.status ? `${listboxId}-status-${index}` : undefined}
                    disabled={option.disabled}
                    className={`quotation-figma-dropdown-option${
                      isSelected ? " is-selected" : ""
                    }`}
                    key={`${String(option.value)}-${index}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange?.({ target: { value: option.value } });
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                  >
                    <span>{option.label}</span>
                    {option.status && <span id={`${listboxId}-status-${index}`} className="themed-select-option-status">{option.status}</span>}
                    {isSelected && <LuCheck />}
                  </button>
                );
              })}
          </div>
        </CatalogueModalDropdownMenu>
      )}
    </div>
  );
}
